import { geohash7Center } from '$lib/fleet';
import { getSupabase } from '$lib/supabase';

type HeatPoint = [number, number, number];
type HeatResolution = 'raw' | 'hourly' | 'daily';

/** One row from get_traffic_heat — an aggregated geohash7 cell. */
interface TrafficHeatRow {
  cell: string;
  weight: number;
  pings: number;
  units: number;
  resolution: HeatResolution;
}

let pings = $state<HeatPoint[]>([]);
let resolution = $state<HeatResolution | null>(null);
let windowH = $state(6);
let loading = $state(false);
let loadedOnce = false;

async function fetchHeat(fromISO: string, toISO: string): Promise<TrafficHeatRow[]> {
  const sb = getSupabase();
  if (!sb) return [];
  const { data, error } = await sb.rpc('get_traffic_heat', { p_from: fromISO, p_to: toISO });
  if (error) throw error;
  return (data ?? []) as TrafficHeatRow[];
}

/** Cell weights are a sum over the window (raw ping weight, or many pings'
 *  worth once binned) — normalize to the batch max so leaflet.heat's 0-1
 *  intensity scale isn't oversaturated by a long window's larger sums. */
function toPings(rows: TrafficHeatRow[]): HeatPoint[] {
  const max = Math.max(1, ...rows.map((r) => r.weight));
  return rows.map((r) => {
    const { lat, lng } = geohash7Center(r.cell);
    return [lat, lng, r.weight / max];
  });
}

export const bikes = {
  get pings() {
    return pings;
  },
  get resolution() {
    return resolution;
  },
  get windowH() {
    return windowH;
  },
  get loading() {
    return loading;
  },
  async load(): Promise<void> {
    loading = true;
    try {
      const to = new Date();
      const from = new Date(to.getTime() - windowH * 3600 * 1000);
      const rows = await fetchHeat(from.toISOString(), to.toISOString());
      pings = toPings(rows);
      resolution =
        rows[0]?.resolution ?? (windowH <= 48 ? 'raw' : windowH <= 24 * 14 ? 'hourly' : 'daily');
      loadedOnce = true;
    } catch (err) {
      console.error('bikes.load failed:', err);
    } finally {
      loading = false;
    }
  },
  setWindow(h: number) {
    if (h === windowH) return;
    windowH = h;
    void this.load();
  },
  ensureLoaded() {
    if (!loadedOnce && !loading) void this.load();
  },
};
